import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { useBlogSitemapImportMutation, useBlogSitemapPreviewMutation } from '../../../../queries/Blog';
import { baseStyleSheet } from '../../../styles/styles';
import { Button } from '../../Button';
import { HelpTooltip } from '../../HelpTooltip';
import { asModalComponent } from '../../Modal';
import { SparklesIcon } from '../../svgs/icons/SparklesIcon';
import { TextInput } from '../../TextInput';
import { styleSheet } from './styles';

interface SitemapToRssFormProps {
	onFinish?: () => void;
}

interface SitemapFormData {
	sitemap: string;
	contentSelector: string;
	titleSelector: string;
	postImageSelector: string;
	postContentSelector: string;
	locationPrefix: string;
	pageLimit: string;
}

export const SitemapToRssForm = (props: SitemapToRssFormProps) => {
	const errorMessages = useErrorMessages();
	const [formData, setFormData] = React.useState<SitemapFormData>({
		sitemap: '',
		contentSelector: '',
		titleSelector: '',
		postImageSelector: '',
		postContentSelector: '',
		locationPrefix: '',
		pageLimit: '10',
	});
	const [isPreviewMode, setIsPreviewMode] = React.useState(false);
	const [_importJobId, setImportJobId] = React.useState<string>('');

	const onError = (error: Api.IOperationResultNoValue) => {
		errorMessages.pushApiError(error);
	};

	const onPreviewSuccess = (_: string) => {
		setIsPreviewMode(true);
		toast.success('Sitemap preview generated successfully');
	};

	const onImportSuccess = (job: any) => {
		setImportJobId(job.id);
		toast.success(`Sitemap import started. Job ID: ${job.id}`);
		props.onFinish?.();
	};

	const previewMutation = useBlogSitemapPreviewMutation({
		onSuccess: onPreviewSuccess,
		onError,
	});

	const importMutation = useBlogSitemapImportMutation({
		onSuccess: onImportSuccess,
		onError,
	});

	const handleInputChange = (field: keyof SitemapFormData, value: string) => {
		setFormData(prev => ({ ...prev, [field]: value }));
	};

	const validateForm = (): boolean => {
		if (!formData.sitemap || !formData.sitemap.match(/^(http|https):\/\/[^ "]+$/)) {
			toast.error('Invalid sitemap URL');
			return false;
		}

		return true;
	};

	const handlePreviewClick = async () => {
		if (!validateForm()) return;

		const params = {
			sitemap: formData.sitemap,
			contentSelector: formData.contentSelector || undefined,
			titleSelector: formData.titleSelector || undefined,
			postImageSelector: formData.postImageSelector || undefined,
			postContentSelector: formData.postContentSelector || undefined,
			locationPrefix: formData.locationPrefix || undefined,
			PageLimit: formData.pageLimit ? parseInt(formData.pageLimit, 10) : 10,
		};

		previewMutation.mutate(params);
	};

	const handleGenerateClick = async () => {
		if (!validateForm()) return;

		const params = {
			sitemap: formData.sitemap,
			contentSelector: formData.contentSelector || undefined,
			titleSelector: formData.titleSelector || undefined,
			postImageSelector: formData.postImageSelector || undefined,
			postContentSelector: formData.postContentSelector || undefined,
			locationPrefix: formData.locationPrefix || undefined,
		};

		importMutation.mutate(params);
	};

	if (isPreviewMode) {
		return (
			<div className={css(styleSheet.container)}>
				<div className={css(styleSheet.previewHeader)}>
					<h3>RSS Preview</h3>
					<Button kind='reverse' label='Back to Form' onClick={() => setIsPreviewMode(false)} />
				</div>
				<div className={css(styleSheet.previewContent)}>
					<pre>{previewMutation.data}</pre>
				</div>
				<div className={css(styleSheet.buttonRow)}>
					<Button
						kind='primary'
						label={importMutation.isPending ? 'Generating...' : 'Generate RSS Feed'}
						accessory={<SparklesIcon />}
						onClick={handleGenerateClick}
						disabled={importMutation.isPending}
						isLoading={importMutation.isPending}
					/>
				</div>
			</div>
		);
	}

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.scrollableContent)}>
				<div>
					<label className={css(styleSheet.label)} htmlFor='sitemapInput'>
						Sitemap URL *
					</label>
					<TextInput
						id='sitemapInput'
						value={formData.sitemap}
						onChange={e => handleInputChange('sitemap', e.target.value)}
						placeholder='https://example.com/sitemap.xml'
						type='text'
					/>
				</div>

				<div className={css(styleSheet.sectionHeader)}>
					<h4>CSS Selectors (optional)</h4>
					<p className={css(styleSheet.sectionDescription)}>
						Use CSS selectors to target specific content areas. Supports class (.class-name) and ID (#element-id)
						selectors.
					</p>
				</div>

				<div>
					<label className={css(styleSheet.label)} htmlFor='contentSelectorInput'>
						<div className={css(baseStyleSheet.horizontalStack)}>
							Content Selector
							<HelpTooltip className={css(styleSheet.tooltip)}>
								<div className={css(styleSheet.tooltipContent)}>
									Targets the main content area of each page. Use this for the overall article or blog post container.
									<br />
									<br />
									Example: If your blog posts are wrapped in &lt;div class=&quot;main-content&quot;&gt;, use
									&quot;.main-content&quot;
								</div>
							</HelpTooltip>
						</div>
					</label>
					<TextInput
						id='contentSelectorInput'
						value={formData.contentSelector}
						onChange={e => handleInputChange('contentSelector', e.target.value)}
						placeholder='.main-content'
						type='text'
					/>
				</div>

				<div>
					<label className={css(styleSheet.label)} htmlFor='titleSelectorInput'>
						Title Selector
					</label>
					<TextInput
						id='titleSelectorInput'
						value={formData.titleSelector}
						onChange={e => handleInputChange('titleSelector', e.target.value)}
						placeholder='.post-title'
						type='text'
					/>
				</div>

				<div>
					<label className={css(styleSheet.label)} htmlFor='postImageSelectorInput'>
						<div className={css(baseStyleSheet.horizontalStack)}>
							Post Image Selector
							<HelpTooltip className={css(styleSheet.tooltip)}>
								<div className={css(styleSheet.tooltipContent)}>
									Targets the featured image or main image for each blog post. This image will be included in the RSS
									feed.
									<br />
									<br />
									Example: If your featured images use &lt;img class=&quot;featured-image&quot;&gt;, use
									&quot;.featured-image&quot;
								</div>
							</HelpTooltip>
						</div>
					</label>
					<TextInput
						id='postImageSelectorInput'
						value={formData.postImageSelector}
						onChange={e => handleInputChange('postImageSelector', e.target.value)}
						placeholder='.featured-image'
						type='text'
					/>
				</div>

				<div>
					<label className={css(styleSheet.label)} htmlFor='postContentSelectorInput'>
						<div className={css(baseStyleSheet.horizontalStack)}>
							Post Content Selector
							<HelpTooltip className={css(styleSheet.tooltip)}>
								<div className={css(styleSheet.tooltipContent)}>
									More specific than Content Selector - targets just the article text content, excluding headers,
									sidebars, and metadata.
									<br />
									<br />
									Example: If your article text is in &lt;div class=&quot;post-content&quot;&gt;, use
									&quot;.post-content&quot;
								</div>
							</HelpTooltip>
						</div>
					</label>
					<TextInput
						id='postContentSelectorInput'
						value={formData.postContentSelector}
						onChange={e => handleInputChange('postContentSelector', e.target.value)}
						placeholder='.post-content'
						type='text'
					/>
				</div>

				<div>
					<label className={css(styleSheet.label)} htmlFor='locationPrefixInput'>
						<div className={css(baseStyleSheet.horizontalStack)}>
							Location Prefix Filter (optional)
							<HelpTooltip className={css(styleSheet.tooltip)}>
								<div className={css(styleSheet.tooltipContent)}>
									Only process pages from the sitemap that start with this URL. Useful for filtering to just blog posts.
									<br />
									<br />
									Example: &quot;https://example.com/blog/&quot; will only process URLs that start with that path.
								</div>
							</HelpTooltip>
						</div>
					</label>
					<TextInput
						id='locationPrefixInput'
						value={formData.locationPrefix}
						onChange={e => handleInputChange('locationPrefix', e.target.value)}
						placeholder='https://example.com/blog/'
						type='text'
					/>
				</div>

				<div>
					<label className={css(styleSheet.label)} htmlFor='pageLimitInput'>
						Page Limit (preview only)
					</label>
					<TextInput
						id='pageLimitInput'
						value={formData.pageLimit}
						onChange={e => handleInputChange('pageLimit', e.target.value)}
						placeholder='10'
						type='number'
					/>
				</div>
			</div>

			<div className={css(styleSheet.buttonRow)}>
				<Button
					kind='reverse'
					label={previewMutation.isPending ? 'Previewing...' : 'Preview RSS'}
					onClick={handlePreviewClick}
					disabled={!formData.sitemap || previewMutation.isPending}
					isLoading={previewMutation.isPending}
				/>
				<Button
					kind='primary'
					label={importMutation.isPending ? 'Generating...' : 'Generate RSS Feed'}
					accessory={<SparklesIcon />}
					onClick={handleGenerateClick}
					disabled={!formData.sitemap || importMutation.isPending}
					isLoading={importMutation.isPending}
				/>
			</div>
		</div>
	);
};

export const SitemapToRssModal = asModalComponent(SitemapToRssForm, {
	shouldCloseOnOverlayClick: false,
	useDefaultHeader: true,
	className: 'sitemap-to-rss-modal',
});
